import { DataStore } from "../store";
import { DraggableKeys, ModalKeys } from "../../interfaces/config";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { SearchTab } from "../../interfaces/headerSearch";
import { mchenryServiceCoreUrl } from "../../main";

export type ResultsState = {
  detailsSelection: any | null;
  activeResultsType: string;
  results: any[];
  selectedResults: any[];
  isResultsLoading: boolean;
  activeSearchTab: string;
  salesUrl: string;
  searchValue: string;
  selectedParcelItems: any[];
};

export type ResultsActions = {
  startSearch: (
    where: string,
    searchTab: SearchTab,
    callback?: (res: any) => void
  ) => void;
  setActiveResultsType: (type: string) => void;
  setResults: (results: any[]) => void;
  rehighlightResults: () => void;
  setSelectedResults: (results: any[]) => void;
  addResults: (results: any[]) => void;
  getMostCurrentDetailsByPin: (pin: string) => Promise<any>;
  getDetailsByPin: (pin: string) => Promise<any>;
  setDetailsSelection: (selection: any) => void;
  setActiveSearchTab: (tabVal: string) => void;
  setSalesUrl: (url: string) => void;
  exportSelectedResults: () => void;
  removeUnselectedResults: () => void;
  setSearchValue: (value: string) => void;
  setSelectedParcelItems: (items: any[]) => void;
};

export const createResultsSlice = (
  set,
  get
): ResultsState & ResultsActions => ({
  detailsSelection: null,
  salesUrl: "",
  activeResultsType: "",
  setActiveResultsType: (type) => set({ activeResultsType: type }),
  getMostCurrentDetailsByPin: async (pin: string) => {
    const baseUrl = `${mchenryServiceCoreUrl}/WedgeDevnetService.svc/rest/GetDetail`;

    // const currentYear = new Date().getFullYear() - 1;
    const url = `${baseUrl}/${pin}/`;
    const res = await fetch(url);
    const data = await res.json();
    // const latestData =

    return { ParcelNumber: data.PIN, ...data.Parcels[0].Value };
    // data.ParcelNumber = pin;
    // data.Year = currentYear;

    return data;
  },
  getDetailsByPin: async (pin: string) => {
    try {
      const url = `${mchenryServiceCoreUrl}/WedgeDevnetService.svc/rest/GetDetail/${pin}`;
      const res = await fetch(url);
      const data = await res.json();
      return data;
    } catch (error) {
      console.warn("Error fetching details by pin:", error);
      return null;
    }
  },
  setSalesUrl: (url) => set({ salesUrl: url }),
  results: [],
  setResults: async (results, highlightAll = false) => {
    const basemap = get().globalSelectedBasemap;
    const isAerialBasemap = basemap?.id?.includes("aerial");
    const { primaryLayer } = get();
    const isFeatureLayer = primaryLayer instanceof FeatureLayer;

    if (isFeatureLayer) {
      const allOids = await primaryLayer.queryObjectIds();
      primaryLayer.applyEdits({
        deleteFeatures: allOids.map((oid) => ({
          objectId: oid,
        })),
      });
    }

    if (results.length === 0) {
      get().deactivateTool(DraggableKeys.RESULTS_DRAGGABLE);
      set({ results: [] });
    } else {
      get().activateTool(DraggableKeys.RESULTS_DRAGGABLE);
      get()
        .mapView.goTo(results, { animate: false })
        .then(() => {
          get().mapView.zoom = get().mapView.zoom - 1;
        });
      set({ results });

      if (isFeatureLayer) {
        await primaryLayer.applyEdits({
          addFeatures: results,
        });
        primaryLayer.refresh();
      } else {
        const highlightLayer = get().highlightLayer;

        let highlightSymbol;
        if (isAerialBasemap) {
          highlightSymbol = {
            type: "simple-fill",
            color: [102, 245, 233, 0],
            outline: {
              color: [102, 245, 233, 255],
              width: 2,
            },
          };
        } else {
          highlightSymbol = {
            type: "simple-fill",
            color: [102, 245, 233, 0.35],
            outline: {
              color: [102, 245, 233, 255],
              width: 2,
            },
          };
        }

        const _results = results.map((r) => ({
          geometry: r.geometry,
          symbol: highlightSymbol,
          attributes: r.attributes,
        }));
        highlightLayer.removeAll();
        highlightLayer.addMany(_results);
      }

      if (results.length > 0 && highlightAll) {
        const oids = results.map((r) => r.attributes.OBJECTID);
        get().addHighlights(oids);
      }
    }
  },

  rehighlightResults: () => {
    const { globalSelectedBasemap, results, highlightLayer } = get();
    if (!results.length) return;

    const isAerialBasemap = globalSelectedBasemap?.id?.includes("aerial");
    const highlightSymbol = isAerialBasemap
      ? {
          type: "simple-fill",
          color: [102, 245, 233, 0],
          outline: {
            color: [102, 245, 233, 255],
            width: 2,
          },
        }
      : {
          type: "simple-fill",
          color: [102, 245, 233, 0.35],
          outline: {
            color: [102, 245, 233, 255],
            width: 2,
          },
        };

    const graphics = results.map((r: any) => ({
      geometry: r.geometry,
      symbol: highlightSymbol,
      attributes: r.attributes,
    }));

    highlightLayer.removeAll();
    highlightLayer.addMany(graphics);
  },

  selectedResults: [],
  setSelectedResults: async (results) => {
    const primaryLayer = get().primaryLayer;
    if (primaryLayer instanceof FeatureLayer) {
      const featureIdField = "FEATUREID";
      const featureIds = results.map((r) => r[featureIdField]);
      const oids = await primaryLayer.queryObjectIds({
        where: `${featureIdField} IN (${featureIds.join(",")})`,
      });
      get().addHighlights(oids);
      set({ selectedResults: results });
    }
  },
  addResults: (results) => {
    if (results.length === 0) {
      get().deactivateTool(DraggableKeys.RESULTS_DRAGGABLE);
      set({ results: [] });
    } else {
      get().activateTool(DraggableKeys.RESULTS_DRAGGABLE);
      const _results = results.filter((r) => {
        const existingOids = get().results.map((r) => r.attributes.OBJECTID);
        return !existingOids.includes(r.attributes.OBJECTID);
      });
      get().setResults([...get().results, ..._results]);
    }
  },
  isResultsLoading: false,
  setDetailsSelection: (selection: any) => {
    if (!selection) {
      get().deactivateTool(DraggableKeys.PARCEL_DETAILS_DRAGGABLE);
      return set({ detailsSelection: null });
    }
    get().activateTool(DraggableKeys.PARCEL_DETAILS_DRAGGABLE);
    set({ detailsSelection: selection });
  },
  startSearch: async (where, searchTab, callback) => {
    const {
      layerId,
      queryUrl,
      getRestUrl,
      getWhereClause,
      extraAttributesLookupKey,
      getExtraAttributesLookup,
    } = searchTab;

    set({ isResultsLoading: true, activeResultsType: searchTab.value });
    const store = get() as DataStore;
    let searchLayer;

    if (layerId) {
      searchLayer = store.getLayerById(layerId);
    } else if (queryUrl) {
      searchLayer = new FeatureLayer({ url: queryUrl });
    } else if (getRestUrl) {
      const restUrl = await getRestUrl();
      const res = await fetch(restUrl);
      const data = await res.json();
      if (callback) {
        callback(data);
        set({ isResultsLoading: false });
        return;
      }

      get().setResults(data);

      if (data.length === 5000) {
        get().activateTool(ModalKeys.MAX_RESULTS_EXCEEDED);
      }
    }
    if (!searchLayer) {
      console.error("Layer not found", layerId);
      return;
    } else {
      const res = await searchLayer.queryFeatures({
        where,
        outFields: ["*"],
        returnGeometry: true,
      });
      if (getExtraAttributesLookup && extraAttributesLookupKey) {
        const extraAttributesLookup = await getExtraAttributesLookup();
        res.features.forEach((d) => {
          const extraAttributes = extraAttributesLookup.find(
            (a) =>
              a[extraAttributesLookupKey] ===
              d.attributes[extraAttributesLookupKey]
          );
          d.attributes = { ...d.attributes, ...extraAttributes };
        });
      }
      if (callback) {
        callback(res);
        set({ isResultsLoading: false });
        return;
      }
      get().setResults(res.features);
      if (res.features.length === 5000) {
        get().activateTool(ModalKeys.MAX_RESULTS_EXCEEDED);
      } else if (res.features.length === 0) {
        console.log("No results found");
        get().activateTool(ModalKeys.NO_RESULTS_FOUND);
      }
    }

    set({ isResultsLoading: false });
  },
  activeSearchTab: "parcel",
  setActiveSearchTab: (tab) => set({ activeSearchTab: tab }),
  removeUnselectedResults: async () => {
    const {
      primaryLayer,
      results,
      selectedResults,
      setResults,
      setSelectedOids,
    } = get();
    const oidField = primaryLayer.objectIdField;
    const featureIdField = "FEATUREID";
    const featureIds = selectedResults.map((r) => r[featureIdField]);
    const res = await primaryLayer.queryFeatures({
      where: `${featureIdField} IN (${featureIds.join(",")})`,
      outFields: [oidField, featureIdField],
    });
    const oids = res.features.map((r) => r.attributes[oidField]);
    console.log(oids);

    const newResults = results.filter((r) => {
      const featureId = r.attributes[featureIdField];
      const oidFeature = res.features.find(
        (f) => f.attributes[featureIdField] === featureId
      );
      if (oidFeature) {
        r.attributes[oidField] = oidFeature.attributes[oidField];
      }
      return featureIds.includes(featureId);
    });
    setResults(newResults, true);
    setSelectedOids(oids);
  },
  exportSelectedResults: () => {
    const { selectedResults, results } = get();
    const _resultsToExport =
      selectedResults.length > 0
        ? selectedResults
        : results.map((r) => r.attributes);
    function arrayToCsv(data) {
      return data
        .map(
          (row) =>
            row
              .map(String) // convert every value to String
              .map((v) => v.replaceAll('"', '""')) // escape double quotes
              .map((v) => `"${v}"`) // quote it
              .join(",") // comma-separated
        )
        .join("\r\n"); // rows starting on new lines
    }
    // headers
    const exportData = _resultsToExport.map((r) => {
      delete r.geometry;
      return r;
    });
    const headers = Object.keys(exportData[0]).join(",");
    const rows = arrayToCsv(exportData.map((r) => Object.values(r)));
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "selected_results.csv";
    a.click();
  },
  searchValue: "",
  setSearchValue: (value) => {
    set({ searchValue: value });
  },
  selectedParcelItems: [],
  setSelectedParcelItems: (items) => {
    set({ selectedParcelItems: items });
  },
});
